import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCached, requestTranslation, subscribe } from '@/lib/autoTranslate';

/**
 * Traduce automaticamente CONTENUTI DINAMICI (dal DB) nella lingua attiva.
 * In italiano restituisce il testo originale; in EN/ES usa la traduzione AI
 * (con cache). Uso: <T>{esperienza.title}</T> oppure <T as="span" className="…">…</T>
 */
export function T({ children, as: Tag, fallback, ...rest }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const text = typeof children === 'string'
    ? children
    : Array.isArray(children) ? children.filter((c) => typeof c === 'string').join('') : '';
  const [, force] = useState(0);

  useEffect(() => {
    if (lang === 'it' || !text) return undefined;
    requestTranslation(lang, text);
    const unsub = subscribe(() => force((n) => n + 1));
    return unsub;
  }, [lang, text]);

  const out = lang === 'it' ? text : (getCached(lang, text) ?? fallback ?? text);
  if (Tag) return <Tag {...rest}>{out}</Tag>;
  return <>{out}</>;
}

/**
 * Hook per tradurre stringhe dove non è possibile usare <T> (es. alt, placeholder,
 * title). Ritorna una funzione tr(text) => stringa tradotta (o originale mentre carica).
 */
export function useAutoTranslate() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [, force] = useState(0);

  useEffect(() => {
    if (lang === 'it') return undefined;
    const unsub = subscribe(() => force((n) => n + 1));
    return unsub;
  }, [lang]);

  return (text) => {
    if (lang === 'it' || !text || typeof text !== 'string') return text;
    requestTranslation(lang, text);
    return getCached(lang, text) ?? text;
  };
}

export default T;
